import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HistoryForm, {
  HistoryFormValues,
} from "Components/Admin/AdminOtherDetails/AdminHistory/HistoryForm";

import type { AdminHistory, AdminHistoryDTO } from "Types/Admin/AdminHistoryType";
import {
  useUpdateHistoryMutation,
  useGetHistoryByIdQuery,
  useDeleteHistoryImageMutation,
} from "Services/HistoryApiSlice";
import { notifications } from "@mantine/notifications";
import { Loader, Center } from "@mantine/core";

interface LocationState {
  item?: AdminHistory;
}

const AdminHistoryEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const plantEnv = process.env.REACT_APP_PLANT ?? "";

  const location = useLocation();
  const state = location.state as LocationState;

  const historyId = Number(id);

  
  const {
    data: fetchedItem,
    isLoading: isFetching,
    refetch: refetchHistory,
  } = useGetHistoryByIdQuery(
    { id: historyId, plant: plantEnv },
    { skip: !!state?.item || !historyId }
  );

  const item = state?.item || fetchedItem;

  const [updateHistory, { isLoading: isUpdating }] =
    useUpdateHistoryMutation();
  const [deleteHistoryImage, { isLoading: isDeletingImage }] =
    useDeleteHistoryImageMutation();

  if (isFetching) {
    return (
      <Center style={{ height: 400 }}>
        <Loader />
      </Center>
    );
  }

  if (!item) {
    return (
      <div style={{ padding: "24px 48px" }}>
        <p>History entry not found.</p>
        <button
          onClick={() => navigate("/admin/other-details/history")}
          className="mt-2 px-4 py-2 border rounded text-sm"
        >
          Back to History
        </button>
      </div>
    );
  }

  const initialValues: HistoryFormValues = {
    year: item.year,
    title: item.title,
    description1: item.description1 ?? "",
    description2: item.description2 ?? "",
    description3: item.description3 ?? "",
    imageUrl: item.imageUrl ?? null,
  };

  const handleSubmit = async (
    values: HistoryFormValues,
    file: File | null
  ) => {
    const payload: AdminHistoryDTO = {
      id: item.id,
      plant: item.plant || plantEnv,
      year: values.year ?? item.year,
      title: values.title,
      description1: values.description1,
      description2: values.description2,
      description3: values.description3,
    };

    try {
      const requestPayload: any = { data: payload };

if (file) requestPayload.file = file;

const response: any = await updateHistory(requestPayload).unwrap();

      notifications.show({
        message: response?.message,
        color: "green",
      });

      navigate("/admin/other-details/history");
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleDeleteImage = async () => {
    if (!item?.id) return;

    try {
      const res = await deleteHistoryImage({ id: item.id, plant: item.plant || plantEnv }).unwrap();

      notifications.show({
        message: res.message,
        color: "green",
      });

      if (typeof refetchHistory === "function") {
        try {
          await refetchHistory();
        } catch {
          // ignore refetch errors; delete succeeded
        }
      }
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  return (
    <div style={{ padding: "24px 48px" }}>
      <h3 style={{ marginBottom: 16 }}>Edit History</h3>

      <HistoryForm
        mode="edit"
        initialValues={initialValues}
        loading={isUpdating}
        onSubmit={handleSubmit}
        onCancel={() => navigate("/admin/other-details/history")}
        onDeleteImage={handleDeleteImage}
        isDeletingCoverImage={isDeletingImage}
      />
    </div>
  );
};

export default AdminHistoryEdit;
